const GROCERY_PO_TYPE = 'GROCERY';
const PACKAGING_PO_TYPE = 'PACKAGING';

/**
 * @param {string} poType
 * @returns {boolean}
 */
function supportsExpirationDates(poType) {
    return poType !== PACKAGING_PO_TYPE;
}

/**
 * @param {string} poType
 * @returns {'purchaseOrderMapId' | 'packagingPurchaseOrderMapId'}
 */
function getMapIdField(poType) {
    return poType === PACKAGING_PO_TYPE ? 'packagingPurchaseOrderMapId' : 'purchaseOrderMapId';
}

/**
 * @param {number | string | null | undefined} companySourceId
 * @returns {number | null}
 */
function normalizeCompanySourceId(companySourceId) {
    if (companySourceId == null || companySourceId === '') {
        return null;
    }

    const numericId = Number(companySourceId);
    return Number.isFinite(numericId) ? numericId : null;
}

/**
 * @param {object} map
 * @param {string} poType
 * @returns {number | null}
 */
function getMapIdFromRow(map, poType) {
    if (poType === PACKAGING_PO_TYPE) {
        return map.packagingPurchaseOrderMapId ?? null;
    }

    return map.purchaseOrderMapId ?? null;
}

/**
 * @param {object} line
 * @param {boolean} isFbmMode
 * @returns {Record<string, string>}
 */
function buildLotFields(line, isFbmMode) {
    const trimmedLot = (line.supplierLot ?? '').trim();
    if (!trimmedLot) {
        return {};
    }

    if (isFbmMode) {
        return { partnerLotNumber: trimmedLot };
    }

    return { supplierLotNumber: trimmedLot };
}

/**
 * @param {Object} params
 * @param {string} params.poType
 * @param {Array<{ id: string, lines: Array<{ companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }> }>} params.pallets
 * @param {object[]} params.purchaseOrderMaps
 * @param {boolean} [params.receiveQty0=false]
 * @param {string} [params.overrideExpirationDateBadgeNumber]
 * @param {boolean} [params.isFbmMode=false]
 * @returns {{ receiveQty0: boolean, pallets: Array<{ caseCounts: object[] }>, overrideExpirationDateBadgeNumber?: string }}
 */
function buildPurchaseOrderMapGroupReceivePayload({
    isFbmMode = false,
    overrideExpirationDateBadgeNumber,
    pallets,
    poType,
    purchaseOrderMaps,
    receiveQty0 = false,
}) {
    if (receiveQty0) {
        return { receiveQty0: true, pallets: [] };
    }

    const mapIdField = getMapIdField(poType);
    const includeExpirationDates = supportsExpirationDates(poType);
    const isGrocery = poType === GROCERY_PO_TYPE;

    /**
     * @param {object} line
     * @returns {number}
     */
    const resolveMapId = (line) => {
        const lineSourceId = normalizeCompanySourceId(line.companySourceId);
        if (lineSourceId != null) {
            const map = purchaseOrderMaps.find(
                (entry) => normalizeCompanySourceId(entry.companySourceId) === lineSourceId,
            );
            const mapId = map ? getMapIdFromRow(map, poType) : null;
            if (mapId != null) {
                return mapId;
            }
        }

        if (isFbmMode && purchaseOrderMaps.length === 1) {
            const mapId = getMapIdFromRow(purchaseOrderMaps[0], poType);
            if (mapId != null) {
                return mapId;
            }
        }

        throw new Error(
            `Unable to resolve ${mapIdField} for a pallet line. Select a company source or verify group data.`,
        );
    };

    const submittedMapIds = new Set();
    const apiPallets = pallets.map((pallet) => {
        const caseCounts = [];

        for (const line of pallet.lines) {
            const caseCount = Number(line.numCases) || 0;
            if (caseCount > 0) {
                const mapId = resolveMapId(line);
                submittedMapIds.add(mapId);

                const allocation = {
                    [mapIdField]: mapId,
                    caseCount,
                    ...buildLotFields(line, isFbmMode),
                };

                if (includeExpirationDates && line.expiration) {
                    allocation.expiresOn = line.expiration;
                }

                caseCounts.push(allocation);
            }
        }

        return { caseCounts };
    });

    const expectedMapIds = purchaseOrderMaps
        .map((map) => getMapIdFromRow(map, poType))
        .filter((mapId) => mapId != null);

    const missingMapIds = expectedMapIds.filter((mapId) => !submittedMapIds.has(mapId));
    if (missingMapIds.length > 0) {
        throw new Error(`Missing purchase order map IDs: ${missingMapIds.join(', ')}`);
    }

    const payload = {
        receiveQty0: false,
        pallets: apiPallets,
    };

    if (isGrocery && overrideExpirationDateBadgeNumber) {
        payload.overrideExpirationDateBadgeNumber = overrideExpirationDateBadgeNumber;
    }

    return payload;
}

export { buildPurchaseOrderMapGroupReceivePayload, getMapIdField, supportsExpirationDates };
